import { Stack, Title, Group, Select, Badge, SimpleGrid, Card, Text, Table, Progress, Loader, Alert } from '@mantine/core'
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { gpuApi, nodesApi } from '@/api'
import { GpuAllocationChart } from '@/components/GpuAllocationChart'
import { formatBytes } from '@/utils/format'
import { IconCpu, IconAlertCircle } from '@tabler/icons-react'
import type { GpuDeviceInfo, NodeGpuResponse } from '@/types'

type FilterType = 'all' | 'available' | 'allocated' | 'exclusive' | 'mig' | 'time-sliced'

interface NodeGpuData {
  nodeId: string
  hostname: string
  devices: GpuDeviceInfo[]
}

export function GPUs() {
  const [filter, setFilter] = useState<FilterType>('all')
  const [nodeFilter, setNodeFilter] = useState<string>('all')
  const [gpuTypeFilter, setGpuTypeFilter] = useState<string>('all')

  // Fetch all nodes
  const { data: nodes = [], isLoading: nodesLoading } = useQuery({
    queryKey: ['nodes'],
    queryFn: async () => {
      const { data } = await nodesApi.list()
      return data
    },
    refetchInterval: 10000,
  })

  // Fetch GPU allocations summary
  const { data: allocationsData, isLoading: allocationsLoading } = useQuery({
    queryKey: ['gpu-allocations'],
    queryFn: async () => {
      const { data } = await gpuApi.getAllocations()
      return data
    },
    refetchInterval: 5000,
  })

  // Fetch GPU data for all nodes
  const { data: nodeGpuData = [], isLoading: gpuDataLoading } = useQuery({
    queryKey: ['node-gpus', nodes],
    queryFn: async () => {
      const gpuNodes = nodes.filter(node => {
        // Assuming nodes with GPU have a label or we check via API
        // For now, we'll fetch for all nodes and filter out empty results
        return true
      })

      const results: NodeGpuData[] = []

      for (const node of gpuNodes) {
        try {
          const { data } = await gpuApi.getNodeGpus(node.ID)
          if (data.devices.length > 0) {
            results.push({
              nodeId: node.ID,
              hostname: node.Description.Hostname,
              devices: data.devices,
            })
          }
        } catch (err) {
          // Node doesn't have GPUs or API failed, skip
          console.debug(`No GPUs on node ${node.ID}`)
        }
      }

      return results
    },
    enabled: nodes.length > 0,
    refetchInterval: 10000,
  })

  const isLoading = nodesLoading || allocationsLoading || gpuDataLoading

  // Get all unique GPU types
  const gpuTypes = useMemo(() => {
    const types = new Set<string>()
    nodeGpuData.forEach(node => {
      node.devices.forEach(device => types.add(device.name))
    })
    return Array.from(types).sort()
  }, [nodeGpuData])

  // Filter devices based on selected filters
  const filteredData = useMemo(() => {
    return nodeGpuData
      .filter(nodeData => {
        if (nodeFilter === 'all') return true
        return nodeData.nodeId === nodeFilter
      })
      .map(nodeData => ({
        ...nodeData,
        devices: nodeData.devices.filter(device => {
          // GPU type filter
          if (gpuTypeFilter !== 'all' && device.name !== gpuTypeFilter) {
            return false
          }

          // Availability filter
          if (filter === 'available') return device.allocations.length === 0
          if (filter === 'allocated') return device.allocations.length > 0
          if (filter === 'exclusive') return device.allocations.some(a => a.exclusive)
          if (filter === 'mig') return device.allocations.some(a => a.mig_instance !== null)
          if (filter === 'time-sliced') {
            return device.allocations.length > 0 &&
                   device.allocations.every(a => !a.exclusive && a.mig_instance === null)
          }
          return true
        }),
      }))
      .filter(nodeData => nodeData.devices.length > 0)
  }, [nodeGpuData, filter, nodeFilter, gpuTypeFilter])

  // Calculate statistics
  const stats = useMemo(() => {
    const totalDevices = nodeGpuData.reduce((sum, node) => sum + node.devices.length, 0)
    const availableDevices = nodeGpuData.reduce(
      (sum, node) => sum + node.devices.filter(d => d.allocations.length === 0).length,
      0
    )
    const exclusiveDevices = nodeGpuData.reduce(
      (sum, node) => sum + node.devices.filter(d => d.allocations.some(a => a.exclusive)).length,
      0
    )
    const migDevices = nodeGpuData.reduce(
      (sum, node) => sum + node.devices.filter(d => d.allocations.some(a => a.mig_instance)).length,
      0
    )
    const timeSlicedDevices = nodeGpuData.reduce(
      (sum, node) => sum + node.devices.filter(d =>
        d.allocations.length > 0 &&
        d.allocations.every(a => !a.exclusive && !a.mig_instance)
      ).length,
      0
    )

    const totalMemoryMB = nodeGpuData.reduce(
      (sum, node) => sum + node.devices.reduce((s, d) => s + d.memory_mb, 0),
      0
    )
    const allocatedMemoryMB = nodeGpuData.reduce(
      (sum, node) => sum + node.devices.reduce(
        (s, d) => s + d.allocations.reduce((a, alloc) => a + alloc.memory_allocated_mb, 0),
        0
      ),
      0
    )

    return {
      totalDevices,
      availableDevices,
      allocatedDevices: totalDevices - availableDevices,
      exclusiveDevices,
      migDevices,
      timeSlicedDevices,
      totalMemoryMB,
      allocatedMemoryMB,
      memoryUtilization: totalMemoryMB > 0 ? (allocatedMemoryMB / totalMemoryMB) * 100 : 0,
    }
  }, [nodeGpuData])

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text>Loading GPU resources...</Text>
      </Stack>
    )
  }

  if (nodeGpuData.length === 0) {
    return (
      <Stack gap="lg">
        <Title order={2}>GPU Resources</Title>
        <Alert icon={<IconAlertCircle />} title="No GPUs Found" color="blue">
          No GPU devices were detected in the cluster. GPUs will appear here when nodes with GPU hardware are added.
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Title order={2}>GPU Resources</Title>
        <Group>
          <Badge color="blue" variant="light" leftSection={<IconCpu size={14} />}>
            {stats.totalDevices} GPUs
          </Badge>
          <Badge color="green" variant="light">
            {stats.availableDevices} Available
          </Badge>
          <Badge color="orange" variant="light">
            {stats.allocatedDevices} Allocated
          </Badge>
        </Group>
      </Group>

      {/* Summary Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed" mb="xs">Total GPUs</Text>
          <Text size="xl" fw={700}>{stats.totalDevices}</Text>
          <Text size="xs" c="dimmed" mt="xs">
            Across {nodeGpuData.length} node{nodeGpuData.length !== 1 ? 's' : ''}
          </Text>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed" mb="xs">Memory Utilization</Text>
          <Text size="xl" fw={700}>{stats.memoryUtilization.toFixed(1)}%</Text>
          <Progress value={stats.memoryUtilization} size="sm" mt="xs" />
          <Text size="xs" c="dimmed" mt="xs">
            {formatBytes(stats.allocatedMemoryMB * 1024 * 1024)} / {formatBytes(stats.totalMemoryMB * 1024 * 1024)}
          </Text>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed" mb="xs">Allocation Types</Text>
          <Stack gap="xs" mt="xs">
            <Group justify="space-between">
              <Text size="xs">Exclusive</Text>
              <Badge size="xs" variant="light" color="red">{stats.exclusiveDevices}</Badge>
            </Group>
            <Group justify="space-between">
              <Text size="xs">Time-Sliced</Text>
              <Badge size="xs" variant="light" color="orange">{stats.timeSlicedDevices}</Badge>
            </Group>
            <Group justify="space-between">
              <Text size="xs">MIG</Text>
              <Badge size="xs" variant="light" color="blue">{stats.migDevices}</Badge>
            </Group>
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="sm" c="dimmed" mb="xs">Cluster Utilization</Text>
          <Text size="xl" fw={700}>
            {stats.totalDevices > 0 ? ((stats.allocatedDevices / stats.totalDevices) * 100).toFixed(1) : 0}%
          </Text>
          <Progress
            value={stats.totalDevices > 0 ? (stats.allocatedDevices / stats.totalDevices) * 100 : 0}
            size="sm"
            mt="xs"
          />
          <Text size="xs" c="dimmed" mt="xs">
            {stats.allocatedDevices} of {stats.totalDevices} GPUs allocated
          </Text>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Group>
        <Select
          placeholder="Filter by status"
          data={[
            { value: 'all', label: 'All GPUs' },
            { value: 'available', label: 'Available' },
            { value: 'allocated', label: 'Allocated' },
            { value: 'exclusive', label: 'Exclusive' },
            { value: 'time-sliced', label: 'Time-Sliced' },
            { value: 'mig', label: 'MIG Partitioned' },
          ]}
          value={filter}
          onChange={(value) => setFilter(value as FilterType)}
          w={200}
        />

        <Select
          placeholder="Filter by node"
          data={[
            { value: 'all', label: 'All Nodes' },
            ...nodeGpuData.map(node => ({
              value: node.nodeId,
              label: node.hostname,
            })),
          ]}
          value={nodeFilter}
          onChange={(value) => setNodeFilter(value || 'all')}
          w={200}
        />

        {gpuTypes.length > 1 && (
          <Select
            placeholder="Filter by GPU type"
            data={[
              { value: 'all', label: 'All Types' },
              ...gpuTypes.map(type => ({
                value: type,
                label: type,
              })),
            ]}
            value={gpuTypeFilter}
            onChange={(value) => setGpuTypeFilter(value || 'all')}
            w={250}
          />
        )}
      </Group>

      {/* GPU Devices */}
      {filteredData.length === 0 ? (
        <Alert icon={<IconAlertCircle />} title="No GPUs Match Filters" color="blue">
          Try adjusting your filters to see GPU resources.
        </Alert>
      ) : (
        filteredData.map(nodeData => (
          <div key={nodeData.nodeId}>
            <Text size="lg" fw={600} mb="md">
              {nodeData.hostname}
              <Badge ml="sm" variant="light">
                {nodeData.devices.length} GPU{nodeData.devices.length !== 1 ? 's' : ''}
              </Badge>
            </Text>
            <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="md">
              {nodeData.devices.map(device => (
                <GpuAllocationChart
                  key={device.id}
                  device={device}
                  nodeId={nodeData.nodeId}
                />
              ))}
            </SimpleGrid>
          </div>
        ))
      )}

      {/* Global Allocations Table */}
      {allocationsData && allocationsData.allocations.length > 0 && (
        <div>
          <Title order={3} mb="md">Active GPU Allocations</Title>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Task ID</Table.Th>
                  <Table.Th>Node</Table.Th>
                  <Table.Th>GPU Device</Table.Th>
                  <Table.Th>Memory</Table.Th>
                  <Table.Th>Compute</Table.Th>
                  <Table.Th>Type</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {allocationsData.allocations.map((alloc, idx) => (
                  <Table.Tr key={idx}>
                    <Table.Td>
                      <Text size="sm" ff="monospace">{alloc.task_id.substring(0, 12)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {nodeGpuData.find(n => n.nodeId === alloc.node_id)?.hostname || alloc.node_id.substring(0, 12)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{alloc.device_name}</Text>
                      <Text size="xs" c="dimmed">{alloc.device_id.substring(0, 12)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatBytes(alloc.memory_allocated_mb * 1024 * 1024)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{(alloc.compute_fraction * 100).toFixed(0)}%</Text>
                    </Table.Td>
                    <Table.Td>
                      {alloc.exclusive ? (
                        <Badge color="red" variant="light">Exclusive</Badge>
                      ) : alloc.mig_instance ? (
                        <Badge color="blue" variant="light">MIG</Badge>
                      ) : (
                        <Badge color="orange" variant="light">Shared</Badge>
                      )}
                      {alloc.mig_instance && (
                        <Text size="xs" c="dimmed">{alloc.mig_instance}</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        </div>
      )}
    </Stack>
  )
}
